import { UserGroup as RestUserGroup } from "../rest-model/UserGroup";
import { User as RestUser } from "../rest-model/User";
import { ProductInstance as RestProductInstance } from "../rest-model/ProductInstance";
import { IdentityProvider as RestIdentityProvider } from "../rest-model/IdentityProvider";
import { OrganizationProductInstances as RestOrganizationProductInstances } from "../rest-model/OrganizationProductInstances";
import { UserInstancePermission as RestUserInstancePermission } from "../rest-model/UserInstancePermission";
import { Role as RestRole } from "../rest-model/Role";
import { Organization as RestOrganization } from "../rest-model/Organization";
import { ProductAttribute as RestAttribute } from "../rest-model/ProductAttribute";
import { Collection } from "../domain/Collection";
import {
    ADMINCENTER_PRODUCT_ID,
    ALL,
    ALL_PAGES_SIZE,
    ALL_PROJECTS_ID,
    CUSTOMER_DEFINED_INSTANCE_ROLE,
    CUSTOMER_DEFINED_PRODUCT_ROLE,
    DEFAULT_PAGE_SIZE,
    GROUP_TYPES
} from "../constants/constants";
import { RoleCreationParameters } from "../domain/Role";
import { InstancePermission } from "../domain/InstancePermission";
import { GetAttributeParameters } from "../rest-model/GetAttributeParameters";
import { Invitation as RestInvitation } from "../rest-model/Invitation";
import { BulkUpload as RestBulkUpload } from "../rest-model/BulkUpload";
import { IOrganizationProductInstance as RestOrganizationProduct } from "../rest-model/OrganizationProductInstances";
import { Customer as RestCustomer } from "../rest-model/Customer";
import { BillingNotification } from "../rest-model/BillingNotification";
import { LocalLoginSettings } from "../rest-model/LocalLoginSettings";
import { OrganizationSettings as RestOrganizationSettings } from "../rest-model/OrganizationSettings";
import { UNEXPECTED_ERROR } from "../constants/constants";
import { datadogRum } from "@datadog/browser-rum";

interface IRestResponseResult<T> {
    items: T[];
    totalItemCount: number;
    links: ILink[];
}

interface ILink {
    rel: string;
    href: string;
}

export class AuthTokenError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "AuthTokenError";
    }
}

export interface IIdentityRepository {
    getUserGroup({ id }: { id: string }): Promise<RestUserGroup>;

    getUserGroups({
        organizationId,
        query,
        emails,
        includeTeams,
        groupTypes,
        size,
        page,
        productIds,
        properties,
        roleIds,
        attributeIds,
        instanceIds
    }: {
        organizationId: string | null;
        query?: string;
        emails?: string[];
        groupTypes?: string[];
        includeTeams?: boolean;
        size?: number;
        page?: number;
        productIds?: string[];
        properties?: string;
        roleIds?: string[];
        attributeIds?: string[];
        instanceIds?: string[];
    }): Promise<Collection<RestUserGroup>>;

    getUsers({
        organizationId,
        query,
        email,
        userGroupIds,
        includeExternalStatus,
        size,
        page,
        isVirtual
    }: {
        organizationId?: string;
        query?: string;
        email?: string;
        userGroupIds?: string[] | null;
        includeExternalStatus?: boolean;
        size?: number;
        page?: number;
        isVirtual?: boolean;
    }): Promise<Collection<RestUser>>;

    getUsersByPermission({
        organizationId,
        productIds,
        instanceIds,
        attributeIds,
        roleIdFilters,
        query,
        includeExternalStatus,
        isVirtual,
        size,
        page
    }: {
        organizationId: string;
        productIds?: string[];
        instanceIds?: string[];
        attributeIds?: string[];
        roleIdFilters?: string[];
        query?: string;
        includeExternalStatus?: boolean;
        isVirtual?: boolean;
        size?: number;
        page?: number;
    }): Promise<Collection<RestUser>>;

    getUserByEmail(email: string, includeExternalStatus?: boolean): Promise<RestUser>;

    deleteUser({ email }: { email: string }): Promise<void>;

    unlockUser(email: string): Promise<void>;

    activateUser(email: string): Promise<void>;

    addUserGroup(userGroup: RestUserGroup): Promise<RestUserGroup>;

    deleteUserGroup({ userGroupId }: { userGroupId: string }): Promise<void>;

    updateUserGroup(userGroup: RestUserGroup): Promise<RestUserGroup>;

    addUserGroupUsers({
        userGroupId,
        userEmails
    }: {
        userGroupId: string;
        userEmails: string[];
    }): Promise<RestUserGroup>;

    addUserGroupProducts({
        userGroupId,
        instancePermissions
    }: {
        userGroupId: string;
        instancePermissions: InstancePermission[];
    }): Promise<{ userGroup: RestUserGroup; failures: Array<{ instanceId: string; error: Error }> }>;

    updateUserGroupProducts({
        userGroupId,
        instancePermissions
    }: {
        userGroupId: string;
        instancePermissions: InstancePermission[];
    }): Promise<{ userGroup: RestUserGroup; failures: Array<{ instanceId: string; error: Error }> }>;

    addUserGroupsToUser({ email, userGroupIds }: { email: string; userGroupIds: string[] }): Promise<RestUser>;

    deleteUserGroupUsers({ userGroupId, userEmails }: { userGroupId: string; userEmails: string[] }): Promise<any>;

    deleteUserGroupProducts({
        userGroupId,
        instanceIds
    }: {
        userGroupId: string;
        instanceIds: string[];
    }): Promise<{ userGroup: RestUserGroup; failures: { instanceId: string; error: Error }[] }>;

    addUser(user: RestUser): Promise<RestUser>;

    getUserGroupInstances({ userGroupId }: { userGroupId: string }): Promise<Collection<RestProductInstance>>;

    getOrganizationProductInstances({
        organizationId
    }: {
        organizationId: string;
    }): Promise<RestOrganizationProductInstances>;

    getUserGroupByName({
        organizationId,
        name
    }: {
        organizationId: string;
        name: string;
    }): Promise<RestUserGroup | undefined>;

    getUserGroupInstance({
        instanceId,
        userGroupId
    }: {
        instanceId: string;
        userGroupId: string;
    }): Promise<RestProductInstance>;

    // this actually replaces the user...
    updateUser(user: RestUser): Promise<RestUser>;

    updateUserFields(email: string, ...fields: any): Promise<RestUser>;

    addOrganizationIdentityProvider({
        organizationId,
        issuer,
        ssoUrl,
        certificate
    }: {
        organizationId: string;
        issuer: string;
        ssoUrl: string;
        certificate: File;
    }): Promise<RestIdentityProvider>;

    addSAMLIdentityProvider({
        idpName,
        organizationId,
        issuer,
        ssoUrl,
        certificate
    }: {
        idpName: string;
        organizationId: string;
        issuer: string;
        ssoUrl: string;
        certificate: File;
    }): Promise<RestIdentityProvider>;

    uploadSAMLIdentityProviderCertificate({
        organizationId,
        identityProviderId,
        certificate
    }: {
        organizationId: string;
        identityProviderId: string;
        certificate: File;
    }): Promise<{ expiresAt: string | null }>;

    addOIDCIdentityProvider({
        authorizationUrl,
        clientId,
        clientSecret,
        idpName,
        issuerUrl,
        jwksUrl,
        organizationId,
        provider,
        tokenUrl,
        userInfoUrl,
        wellKnownUrl
    }: {
        authorizationUrl?: string;
        clientId: string;
        clientSecret: string;
        idpName: string;
        issuerUrl?: string;
        jwksUrl?: string;
        organizationId: string;
        provider: string;
        tokenUrl?: string;
        userInfoUrl?: string;
        wellKnownUrl?: string;
    }): Promise<RestIdentityProvider>;

    updateSAMLIdentityProvider({
        organizationId,
        id,
        idpName,
        issuer,
        ssoUrl
    }: {
        organizationId: string;
        id: string;
        idpName: string;
        issuer: string;
        ssoUrl: string;
    }): Promise<RestIdentityProvider>;

    deleteOrganizationIdentityProvider({ id, organizationId }: { id: string; organizationId: string }): Promise<any>;

    deleteOrganizationIdentityProviders({ organizationId }: { organizationId: string }): Promise<any>;

    getOrganizationIdentityProviders({
        organizationId
    }: {
        organizationId: string;
    }): Promise<RestIdentityProvider[] | null>;

    getOrganizationDomains({ organizationId }: { organizationId: string }): Promise<string[]>;

    updateOrganization({ organizationId, mfaEnabled }: { organizationId: string; mfaEnabled?: boolean }): Promise<any>;

    updateOrganizationDomains({
        organizationId,
        domains
    }: {
        organizationId: string;
        domains: string[];
    }): Promise<string[]>;

    getUserAdminCenterInstancePermissions({
        email,
        orgId
    }: {
        email: string;
        orgId?: string;
    }): Promise<RestUserInstancePermission[]>;

    getUserInstancePermissions({ email }: { email: string }): Promise<RestUserInstancePermission[]>;

    getRoles({
        organizationId,
        productId,
        instanceId,
        size,
        page
    }: {
        organizationId: string;
        productId?: string;
        projectId?: string;
        instanceId?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestRole>>;

    searchRoles({
        organizationId,
        productId,
        instanceId,
        searchQuery,
        size,
        page
    }: {
        organizationId: string;
        productId?: string;
        instanceId?: string;
        searchQuery?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestRole>>;

    createRole(roleDetails: RoleCreationParameters): Promise<RestRole>;
    deleteRole({ roleId }: { roleId: string }): Promise<void>;
    updateRole(roleDetails: RoleCreationParameters): Promise<RestRole>;

    getAttributes(parameters: GetAttributeParameters): Promise<Collection<RestAttribute>>;

    getAttribute(id: string): Promise<RestAttribute>;

    getInvitations({
        createdBy,
        inviteeEmail,
        organizationId,
        size,
        page
    }: {
        inviteeEmail?: string;
        createdBy?: string;
        organizationId?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestInvitation>>;

    createInvitation(invitation: RestInvitation): Promise<RestInvitation>;

    revokeInvitation(invitationId: string, requireAcceptance: boolean): Promise<void>;

    resendInvitation(invitationId: string): Promise<void>;

    updateInstance({ instance }: { instance: Partial<RestOrganizationProduct> }): Promise<RestProductInstance>;

    searchCustomers({ query }: { query: string }): Promise<Collection<RestCustomer>>;

    getOrganization({ id }: { id: string }): Promise<RestOrganization>;

    getProviders(): Promise<string[]>;

    getUserListByProduct({
        organizationId,
        page,
        productIds,
        searchQuery
    }: {
        organizationId: string;
        page?: number;
        productIds: string[];
        searchQuery?: string;
    }): Promise<Collection<RestUser>>;

    getUserListByInstance({
        attributeIds,
        instanceIds,
        organizationId,
        page,
        roleIds,
        searchQuery
    }: {
        attributeIds?: string[];
        instanceIds: string[];
        organizationId?: string | undefined;
        page?: number;
        roleIds?: string[];
        searchQuery?: string;
    }): Promise<Collection<RestUser>>;

    getUserListByProject({
        attributeIds,
        organizationId,
        roleIds,
        page,
        searchQuery
    }: {
        organizationId: string;
        attributeIds: string[];
        roleIds?: string[];
        page?: number;
        searchQuery?: string;
    }): Promise<Collection<RestUser>>;

    getUserGroupsByInstance({
        attributeIds,
        groupTypes,
        instanceIds,
        organizationId,
        roleIds
    }: {
        attributeIds?: string[];
        groupTypes?: string[];
        instanceIds: string[];
        organizationId?: string | undefined;
        roleIds?: string[];
    }): Promise<Collection<RestUserGroup>>;

    getUserGroupsByAttributeId({
        organizationId,
        attributeIds,
        groupTypes
    }: {
        organizationId: string;
        attributeIds: string[];
        groupTypes?: string[];
    }): Promise<Collection<RestUserGroup>>;

    migrateUser({ email, organizationId }: { email: string; organizationId: string }): Promise<RestUser>;

    updateIdentityProviderDomains({
        organizationId,
        identityProviderId,
        domains
    }: {
        organizationId: string;
        identityProviderId: string;
        domains: string[];
    }): Promise<RestIdentityProvider>;

    enableScimForIdentityProvider({
        organizationId,
        identityProviderId
    }: {
        organizationId: string;
        identityProviderId: string;
    }): Promise<RestIdentityProvider>;

    getBillingNotifications({
        organizationId
    }: {
        organizationId: string;
    }): Promise<{ Settings: { billingNotifications: BillingNotification[] } }>;
    updateBillingNotification({
        notificationId,
        organizationId,
        updates
    }: {
        notificationId: string;
        organizationId: string;
        updates: Pick<BillingNotification, "enabled" | "recipients">;
    }): Promise<BillingNotification>;

    getLocalLoginSettings({ organizationId }: { organizationId: string }): Promise<{ Settings: LocalLoginSettings }>;

    getOrganizationSettings({
        organizationId
    }: {
        organizationId: string;
    }): Promise<{ Settings: RestOrganizationSettings }>;

    updateGenAISettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean };
    }): Promise<{ enabled: boolean }>;

    updateLocalLoginSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; mfaEnabled: boolean };
    }): Promise<LocalLoginSettings>;

    updateDeveloperSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; vendorName: string };
    }): Promise<{ enabled: boolean; vendorName: string } | undefined>;

    updateOrganizationSettings({
        organizationId,
        updates
    }: {
        organizationId: string;
        updates: Partial<RestOrganizationSettings>;
    }): Promise<{ Settings: RestOrganizationSettings }>;

    addScimInstance(params: { orgId: string; identityProviderId: string }): Promise<any>;

    removeSCIMForIdentityProvider({
        identityProviderId,
        organizationId
    }: {
        identityProviderId: string;
        organizationId: string;
    }): Promise<void>;

    uploadUsersCsv({
        organizationId,
        file,
        userGroupIds
    }: {
        organizationId: string;
        file: File;
        userGroupIds?: string[];
    }): Promise<void>;

    getBulkUploads({
        organizationId,
        originalFileName,
        size,
        page
    }: {
        organizationId: string;
        originalFileName?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestBulkUpload>>;

    getBulkUpload({
        organizationId,
        fileIdentifier
    }: {
        organizationId: string;
        fileIdentifier: string;
    }): Promise<RestBulkUpload>;
}

export class IdentityRepository implements IIdentityRepository {
    baseURL;
    private getAccessToken: () => Promise<string | null>;

    constructor(baseURL = "/", getAccessToken: () => Promise<string | null>) {
        this.baseURL = baseURL;
        this.getAccessToken = getAccessToken;
    }

    private async resolveAccessToken(): Promise<string> {
        const token = await this.getAccessToken();
        if (!token) {
            throw new AuthTokenError("Unable to obtain access token");
        }
        return token;
    }

    private async request<T>(
        method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH",
        url: string,
        body?: object,
        retryCount?: number
    ): Promise<T> {
        const accessToken = await this.resolveAccessToken();
        const response = await fetch(`${this.baseURL}${url}`, {
            method,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "application/json"
            },
            body: body ? JSON.stringify(body) : null
        });

        //check response.text() first in case it is empty, which causes an error when using response.json()
        const responseText: string = await response.text();

        if (responseText && !response.ok) {
            throw JSON.parse(responseText);
        }

        if (!response.ok) {
            if (retryCount && response.status === 500) {
                return this.request<T>(method, url, body, retryCount - 1);
            } else {
                throw new Error(`Failed request: (${response.status})`);
            }
        }

        const data: T = responseText === "" ? undefined : JSON.parse(responseText);

        return data;
    }

    /**
     * Retries a request with exponential backoff until the validation passes.
     * This handles eventual consistency in distributed systems.
     *
     * @param fetchFn - Function that performs the fetch operation
     * @param validateFn - Function that validates if the response is consistent (returns true if valid)
     * @param maxAttempts - Maximum number of retry attempts (default: 5)
     * @param initialDelayMs - Initial delay in milliseconds (default: 100)
     * @returns The validated response
     */
    private async retryUntilConsistent<T>(
        fetchFn: () => Promise<T>,
        validateFn: (response: T) => boolean,
        maxAttempts: number = 5,
        initialDelayMs: number = 100
    ): Promise<T> {
        let attempt = 0;

        while (attempt < maxAttempts) {
            const response = await fetchFn();

            if (validateFn(response)) {
                return response;
            }

            attempt++;

            // If we've exhausted all attempts, return the last response even if stale
            if (attempt >= maxAttempts) {
                return response;
            }

            // Wait before retrying with exponential backoff
            const delayMs = initialDelayMs * Math.pow(2, attempt - 1);
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }

        // This should never be reached, but TypeScript needs it
        return fetchFn();
    }

    public async getUserGroup({ id }: { id: string }): Promise<RestUserGroup> {
        const userGroup = await this.request<RestUserGroup>("GET", `/api/usergroups/${id}`);
        return new RestUserGroup(userGroup);
    }

    public async getUserGroups({
        organizationId,
        emails,
        groupTypes = [],
        includeTeams = false,
        query,
        size = 20,
        page = 1,
        productIds = [],
        properties,
        roleIds = [],
        attributeIds = [],
        instanceIds = []
    }: {
        organizationId: string | null;
        query?: string;
        emails?: string[];
        groupTypes?: string[];
        includeTeams?: boolean;
        size?: number;
        page?: number;
        productIds?: string[];
        properties?: string;
        roleIds?: string[];
        attributeIds?: string[];
        instanceIds?: string[];
    }): Promise<Collection<RestUserGroup>> {
        const search = new URLSearchParams();
        search.append("organizationId", organizationId || "");
        search.append("size", `${size}`);
        search.append("offset", `${(page - 1) * size}`);
        emails?.forEach((email) => {
            search.append("users", email);
        });

        groupTypes?.forEach((type) => {
            search.append("groupTypes", type);
        });

        productIds?.forEach((type) => {
            search.append("productIds", type);
        });

        roleIds?.forEach((roleId) => {
            search.append("roleIds", roleId);
        });
        attributeIds?.forEach((attId) => {
            search.append("attributeIds", attId);
        });

        if (instanceIds.length) {
            instanceIds?.forEach((instanceId) => {
                search.append("instanceIds", instanceId);
            });
            search.append("matchAnyInstanceId", "true");
        }

        if (query) {
            search.append("name", query.toLowerCase());
            search.append("description", query.toLowerCase());
        }
        // Backend defaults to excludeTeams=true; includeTeams overrides this by explicitly setting excludeTeams=false
        if (includeTeams) {
            search.append("excludeTeams", "false");
        }
        let searchString = search.toString();
        if (properties) {
            searchString += `&${properties}`;
        }

        const userGroups = await this.request<IRestResponseResult<RestUserGroup>>(
            "GET",
            `/api/usergroups?${searchString}`
        );

        return new Collection<RestUserGroup>({
            items: userGroups.items.map((userGroup: RestUserGroup) => new RestUserGroup(userGroup)),
            totalCount: userGroups.totalItemCount
        });
    }

    public async deleteUser({ email }: { email: string }): Promise<void> {
        return await this.request<undefined>("DELETE", `/api/users/${email}`);
    }

    public async unlockUser(email: string): Promise<void> {
        return await this.request<undefined>("POST", `/api/users/${email}/unlock`);
    }

    public async activateUser(email: string): Promise<void> {
        return await this.request<undefined>("POST", `/api/users/${email}/activate`);
    }

    public async addUserGroup(userGroup: RestUserGroup): Promise<RestUserGroup> {
        const userGroupResponse = await this.request<RestUserGroup>("POST", "/api/usergroups", userGroup);

        return new RestUserGroup(userGroupResponse);
    }

    public async deleteUserGroup({ userGroupId }: { userGroupId: string }): Promise<void> {
        return await this.request<undefined>("DELETE", `/api/usergroups/${userGroupId}`);
    }

    public async addUserGroupUsers({
        userGroupId,
        userEmails
    }: {
        userGroupId: string;
        userEmails: string[];
    }): Promise<RestUserGroup> {
        const userGroup = await this.request<IRestResponseResult<RestUserGroup>>(
            "POST",
            `/api/usergroups/${userGroupId}/users`,
            { userEmails },
            3
        );

        return new RestUserGroup(userGroup);
    }

    public async addUserGroupsToUser({
        email,
        userGroupIds
    }: {
        email: string;
        userGroupIds: string[];
    }): Promise<RestUser> {
        const user = await this.request<IRestResponseResult<RestUser>>(
            "POST",
            `/api/users/${email}/usergroups`,
            { userGroupIds },
            3
        );

        return new RestUser(user);
    }

    public async addUserGroupProducts({
        userGroupId,
        instancePermissions
    }: {
        userGroupId: string;
        instancePermissions: InstancePermission[];
    }): Promise<{ userGroup: RestUserGroup; failures: Array<{ instanceId: string; error: Error }> }> {
        const failures: Array<{ instanceId: string; error: Error }> = [];

        // Group permissions by instanceId and merge roleIds when multiple permissions
        // exist for the same instance, ensuring all roles are set in a single PUT request
        const groupedPermissions = new Map<string, Set<string>>();
        instancePermissions.forEach((permission) => {
            if (!groupedPermissions.has(permission.instanceId)) {
                groupedPermissions.set(permission.instanceId, new Set());
            }
            permission.roleIds.forEach((roleId) => {
                groupedPermissions.get(permission.instanceId)!.add(roleId);
            });
        });

        // Add permissions sequentially to avoid race conditions where parallel PUTs overwrite each other
        for (const [instanceId, roleIdsSet] of Array.from(groupedPermissions.entries())) {
            const roleIds = Array.from(roleIdsSet);
            try {
                await this.request<IRestResponseResult<RestUserGroup>>(
                    "PUT",
                    `/api/usergroups/${userGroupId}/instancepermissions/${instanceId}`,
                    { roleIds },
                    3
                );
            } catch (error) {
                failures.push({
                    instanceId,
                    error: error as Error
                });
            }
        }

        // Get successfully added instance IDs
        const failedInstanceIds = new Set(failures.map((f) => f.instanceId));
        const successfullyAddedIds = Array.from(groupedPermissions.keys()).filter((id) => !failedInstanceIds.has(id));

        // Fetch the updated user group with retry logic to handle eventual consistency
        const userGroupResponse = await this.retryUntilConsistent(
            () => this.request<RestUserGroup>("GET", `/api/usergroups/${userGroupId}`, undefined, 3),
            (response) => {
                const group = new RestUserGroup(response);
                // Validate that all successfully added instances are present
                const presentInstanceIds = new Set(
                    group.instancePermissions?.map((ip: { InstanceId: string; Roles?: any[] }) => ip.InstanceId) || []
                );

                // Check if any successfully added instance is missing (stale data)
                const hasStaleData = successfullyAddedIds.some((id) => !presentInstanceIds.has(id));

                // Return true if data is consistent (no stale data)
                return !hasStaleData;
            },
            6,
            100
        );

        const updatedGroup = new RestUserGroup(userGroupResponse);
        // Check for stale data in the final response and mark as failures
        const finalPresentInstanceIds = new Set(
            updatedGroup.instancePermissions?.map((ip: { InstanceId: string; Roles?: any[] }) => ip.InstanceId) || []
        );

        // Any successfully added instances that are still missing indicate stale/inconsistent data
        successfullyAddedIds.forEach((instanceId) => {
            if (!finalPresentInstanceIds.has(instanceId)) {
                datadogRum.addError(
                    `Instance permission ${instanceId} not yet present after addition (eventual consistency timeout)`,
                    { userGroupId }
                );
                failures.push({
                    instanceId,
                    error: new Error("Addition succeeded but changes not yet reflected (eventual consistency)")
                });
            }
        });

        return { userGroup: updatedGroup, failures };
    }
    public async updateUserGroupProducts({
        userGroupId,
        instancePermissions
    }: {
        userGroupId: string;
        instancePermissions: InstancePermission[];
    }): Promise<{ userGroup: RestUserGroup; failures: Array<{ instanceId: string; error: Error }> }> {
        const failures: Array<{ instanceId: string; error: Error }> = [];

        // Group permissions by instanceId and merge roleIds to avoid duplicate API calls
        // and prevent overwrites if multiple permissions exist for the same instance
        const groupedPermissions = new Map<string, Set<string>>();
        instancePermissions.forEach((permission) => {
            if (!groupedPermissions.has(permission.instanceId)) {
                groupedPermissions.set(permission.instanceId, new Set());
            }
            permission.roleIds.forEach((roleId) => {
                groupedPermissions.get(permission.instanceId)!.add(roleId);
            });
        });

        // Update permissions sequentially to avoid race conditions where parallel PUTs overwrite each other
        for (const [instanceId, roleIdsSet] of Array.from(groupedPermissions.entries())) {
            const roleIds = Array.from(roleIdsSet);
            try {
                await this.request<IRestResponseResult<RestUserGroup>>(
                    "PUT",
                    `/api/usergroups/${userGroupId}/instancepermissions/${instanceId}`,
                    { roleIds },
                    3
                );
            } catch (error) {
                failures.push({
                    instanceId,
                    error: error as Error
                });
            }
        }

        // Get successfully updated permissions (as InstancePermission objects for validation)
        const failedInstanceIds = new Set(failures.map((f) => f.instanceId));
        const successfullyUpdatedPermissions: InstancePermission[] = Array.from(groupedPermissions.entries())
            .filter(([instanceId]) => !failedInstanceIds.has(instanceId))
            .map(([instanceId, roleIdsSet]) => ({
                instanceId,
                roleIds: Array.from(roleIdsSet)
            }));

        // Fetch the updated user group with retry logic to handle eventual consistency
        const userGroupResponse = await this.retryUntilConsistent(
            () => this.request<RestUserGroup>("GET", `/api/usergroups/${userGroupId}`, undefined, 3),
            (response) => {
                const group = new RestUserGroup(response);
                // Validate that all successfully updated instances have the correct roleIds
                const responsePermissions = new Map(
                    (group.instancePermissions || []).map((ip: { InstanceId: string; Roles?: any[] }) => [
                        ip.InstanceId,
                        ip.Roles || []
                    ])
                );

                // Check if any successfully updated instance has incorrect roleIds (stale data)
                const hasStaleData = successfullyUpdatedPermissions.some((permission) => {
                    const actualRoleIds = (responsePermissions.get(permission.instanceId) || []) as string[];
                    const expectedRoleIds = permission.roleIds;
                    const actualRoleIdSet = new Set(actualRoleIds);

                    // Check if roleIds match
                    if (expectedRoleIds.length !== actualRoleIds.length) return true;
                    return expectedRoleIds.some((roleId) => !actualRoleIdSet.has(roleId));
                });

                // Return true if data is consistent (no stale data)
                return !hasStaleData;
            },
            6,
            100
        );

        const updatedGroup = new RestUserGroup(userGroupResponse);
        // Check for stale data in the final response and mark as failures
        const finalResponsePermissions = new Map(
            (updatedGroup.instancePermissions || []).map((ip: { InstanceId: string; Roles?: any[] }) => [
                ip.InstanceId,
                ip.Roles || []
            ])
        );

        successfullyUpdatedPermissions.forEach((permission) => {
            const actualRoleIds = (finalResponsePermissions.get(permission.instanceId) || []) as string[];
            const expectedRoleIds = permission.roleIds;
            const actualRoleIdSet = new Set(actualRoleIds);

            // Check if roleIds don't match
            const hasIncorrectRoles =
                expectedRoleIds.length !== actualRoleIds.length ||
                expectedRoleIds.some((roleId) => !actualRoleIdSet.has(roleId));

            if (hasIncorrectRoles) {
                datadogRum.addError(
                    `Instance permission ${permission.instanceId} roles not yet updated (eventual consistency timeout)`,
                    { userGroupId, expectedRoleIds: permission.roleIds, actualRoleIds }
                );
                failures.push({
                    instanceId: permission.instanceId,
                    error: new Error("Update succeeded but changes not yet reflected (eventual consistency)")
                });
            }
        });

        return { userGroup: updatedGroup, failures };
    }

    public async deleteUserGroupUsers({
        userGroupId,
        userEmails
    }: {
        userGroupId: string;
        userEmails: string[];
    }): Promise<any> {
        return await Promise.all(
            userEmails.map(async (email: string) => {
                try {
                    await this.request<void>("DELETE", `/api/usergroups/${userGroupId}/users/${email}`, undefined, 3);
                    return true;
                } catch (e) {
                    return false;
                }
            })
        );
    }

    public async deleteUserGroupProducts({
        userGroupId,
        instanceIds
    }: {
        userGroupId: string;
        instanceIds: string[];
    }): Promise<{
        userGroup: RestUserGroup;
        failures: Array<{ instanceId: string; error: Error }>;
    }> {
        const failures: Array<{ instanceId: string; error: Error }> = [];

        // Process deletions sequentially to avoid race conditions where parallel DELETEs overwrite each other
        for (const instanceId of instanceIds) {
            try {
                await this.request<IRestResponseResult<RestUserGroup>>(
                    "DELETE",
                    `/api/usergroups/${userGroupId}/instancepermissions/${instanceId}`,
                    undefined,
                    3
                );
            } catch (error) {
                datadogRum.addError(
                    `Failed to delete instance permission ${instanceId} from user group ${userGroupId}`,
                    { reason: error }
                );
                failures.push({
                    instanceId,
                    error: error as Error
                });
            }
        }

        // Get the set of successfully deleted instance IDs (excluding failures)
        const failedInstanceIds = new Set(failures.map((f) => f.instanceId));
        const successfullyDeletedIds = instanceIds.filter((id) => !failedInstanceIds.has(id));

        // Fetch the updated user group with retry logic to handle eventual consistency
        const userGroupResponse = await this.retryUntilConsistent(
            () => this.request<RestUserGroup>("GET", `/api/usergroups/${userGroupId}`, undefined, 3),
            (response) => {
                const group = new RestUserGroup(response);
                // Validate that none of the successfully deleted instances are still present
                const remainingInstanceIds = new Set(
                    group.instancePermissions?.map((ip: { InstanceId: string; Roles?: any[] }) => ip.InstanceId) || []
                );

                // Check if any successfully deleted instance is still present (stale data)
                const hasStaleData = successfullyDeletedIds.some((id) => remainingInstanceIds.has(id));

                // Return true if data is consistent (no stale data)
                return !hasStaleData;
            },
            6, // Reduced attempts for faster feedback
            100 // Default delay
        );
        const updatedGroup = new RestUserGroup(userGroupResponse);

        // Check for stale data in the final response and mark as failures
        const finalRemainingInstanceIds = new Set(
            updatedGroup.instancePermissions?.map((ip: { InstanceId: string; Roles?: any[] }) => ip.InstanceId) || []
        );

        // Any successfully deleted instances that are still present indicate stale/inconsistent data
        successfullyDeletedIds.forEach((instanceId) => {
            if (finalRemainingInstanceIds.has(instanceId)) {
                datadogRum.addError(
                    `Instance permission ${instanceId} still present after deletion (eventual consistency timeout)`,
                    { userGroupId }
                );
                failures.push({
                    instanceId,
                    error: new Error("Deletion failed")
                });
            }
        });

        return {
            userGroup: updatedGroup,
            failures
        };
    }

    public async updateUserGroup(userGroup: RestUserGroup): Promise<RestUserGroup> {
        const userGroupResponse = await this.request<IRestResponseResult<RestUserGroup>>(
            "PUT",
            `/api/usergroups/${userGroup.id}`,
            userGroup
        );

        return new RestUserGroup(userGroupResponse);
    }

    public async getUsers({
        organizationId,
        query,
        email,
        userGroupIds,
        includeExternalStatus,
        size = 20,
        page = 1,
        isVirtual
    }: {
        organizationId?: string;
        query?: string;
        email?: string;
        userGroupIds?: string[];
        includeExternalStatus?: boolean;
        size?: number;
        page?: number;
        isVirtual?: boolean;
    }): Promise<Collection<RestUser>> {
        const search = new URLSearchParams();
        if (organizationId) {
            search.append("organizationId", organizationId);
        }

        if (query) {
            search.append("email", query);
            search.append("firstName", query);
            search.append("lastName", query);
        }

        if (email) {
            search.set("email", email);
        }

        if (includeExternalStatus) {
            search.append("includeExternalStatus", `${includeExternalStatus}`);
        }

        if (isVirtual !== undefined) {
            search.append("isVirtual", `${isVirtual}`);
        }

        search.append("matchAnyUserGroups", "true");

        const groupIdChunks = [];
        if (userGroupIds?.length) {
            const chunkSize = 75;
            for (let i = 0; i < userGroupIds?.length; i += chunkSize) {
                groupIdChunks.push(userGroupIds?.slice(i, i + chunkSize));
            }
        }

        let users: RestUser[] = [];
        let totalItemCount = 0;
        if (groupIdChunks.length <= 1) {
            search.append("size", `${size}`);
            search.append("offset", `${(page - 1) * size}`);

            userGroupIds?.forEach((id) => {
                search.append("userGroupIds", id);
            });

            const userList = await this.request<IRestResponseResult<RestUser>>(
                "GET",
                `/api/users?${search.toString()}`
            );

            return new Collection<RestUser>({
                items: userList.items.map((user: RestUser) => new RestUser(user)),
                totalCount: userList.totalItemCount
            });
        } else {
            // THIS IS A TEMPORARY FIX UNTIL WE HAVE AN ENDPOINT FOR GETTING USERS BY INSTANCE OR ATTRIBUTE IDS.
            // In order to do paging with the batch calls, we need to get all users for each call and add them together.
            search.append("size", `${ALL_PAGES_SIZE}`);
            search.append("offset", "0");
            for (const idList of groupIdChunks) {
                search.delete("userGroupIds");
                idList.forEach((id) => {
                    search.append("userGroupIds", id);
                });
                const userChunk = await this.request<IRestResponseResult<RestUser>>(
                    "GET",
                    `/api/users?${search.toString()}`
                );

                totalItemCount += userChunk.totalItemCount;

                users.push(...userChunk.items);
            }

            //use total count and current page to get the list of users for this call.
            const start = (page - 1) * size;
            const end = start + size;
            users = users.slice(start, end);

            return new Collection<RestUser>({
                items: users.map((user: RestUser) => new RestUser(user)),
                totalCount: totalItemCount
            });
        }
    }

    public async getUsersByPermission({
        organizationId,
        productIds,
        instanceIds,
        attributeIds,
        roleIdFilters,
        query,
        includeExternalStatus,
        isVirtual,
        size = DEFAULT_PAGE_SIZE,
        page = 1
    }: {
        organizationId: string;
        productIds?: string[];
        instanceIds?: string[];
        attributeIds?: string[];
        roleIdFilters?: string[];
        query?: string;
        includeExternalStatus?: boolean;
        isVirtual?: boolean;
        size?: number;
        page?: number;
    }): Promise<Collection<RestUser>> {
        const search = new URLSearchParams();

        search.append("size", `${size}`);
        search.append("offset", `${(page - 1) * size}`);

        if (query) {
            search.append("userSearchField", query);
        }

        if (productIds?.length) {
            search.append("PermissionType", "Product");

            productIds?.forEach((id) => {
                search.append("ids", id);
            });
        }

        if (instanceIds?.length) {
            search.append("PermissionType", "Instance");

            instanceIds?.forEach((id) => {
                search.append("ids", id);
            });
        }

        if (attributeIds?.length) {
            search.append("PermissionType", "Attribute");

            attributeIds?.forEach((id) => {
                search.append("ids", id);
            });
        }

        roleIdFilters?.forEach((id) => {
            search.append("roleIds", id);
        });

        if (includeExternalStatus) {
            search.append("includeExternalStatus", `${includeExternalStatus}`);
        }

        if (isVirtual !== undefined) {
            search.append("isVirtual", `${isVirtual}`);
        }

        const userList = await this.request<IRestResponseResult<RestUser>>(
            "GET",
            `/api/organizations/${organizationId}/users?${search.toString()}`
        );

        return new Collection<RestUser>({
            items: userList.items.map((user: RestUser) => new RestUser(user)),
            totalCount: userList.totalItemCount
        });
    }

    public async getUserByEmail(email: string, includeExternalStatus: boolean = false): Promise<RestUser> {
        const user = await this.request<RestUser>(
            "GET",
            `/api/users/${email.toLowerCase()}/?includeExternalStatus=${includeExternalStatus}`
        );

        return new RestUser(user);
    }

    public async addUser(user: RestUser): Promise<RestUser> {
        const userResponse = await this.request<RestUser>("POST", "/api/users", user);

        return new RestUser(userResponse);
    }

    public async getUserGroupInstances({
        userGroupId
    }: {
        userGroupId: string;
    }): Promise<Collection<RestProductInstance>> {
        const userGroupInstances = await this.request<IRestResponseResult<RestProductInstance>>(
            "GET",
            `/api/usergroups/${userGroupId}/instancepermissions?size=${ALL_PAGES_SIZE}`
        );

        return new Collection<RestProductInstance>({
            items: userGroupInstances.items.map((instance: RestProductInstance) => new RestProductInstance(instance)),
            totalCount: userGroupInstances.totalItemCount
        });
    }

    public async getOrganizationProductInstances({
        organizationId
    }: {
        organizationId: string;
    }): Promise<RestOrganizationProductInstances> {
        const orgProductInstances = await this.request<RestOrganizationProductInstances>(
            "GET",
            `/api/organizations/${organizationId}/productinstances`
        );

        return new RestOrganizationProductInstances(orgProductInstances);
    }

    public async getUserGroupInstance({
        instanceId,
        userGroupId
    }: {
        instanceId: string;
        userGroupId: string;
    }): Promise<RestProductInstance> {
        const userGroupInstance = await this.request<RestProductInstance>(
            "GET",
            `/api/usergroups/${userGroupId}/instancepermissions/${instanceId}`
        );

        return new RestProductInstance(userGroupInstance);
    }

    public async getUserGroupByName({
        organizationId,
        name
    }: {
        organizationId: string;
        name: string;
    }): Promise<RestUserGroup | undefined> {
        const params = new URLSearchParams();
        organizationId && params.append("organizationId", organizationId);
        name && params.append("name", name);
        params.append("useExactMatchSearch", `${true}`);

        const userGroups = await this.request<IRestResponseResult<RestUserGroup>>(
            "GET",
            `/api/usergroups?${params.toString()}`
        );

        const listOfGroups = userGroups.items;

        if (listOfGroups && listOfGroups.length > 0) {
            const [userGroup] = listOfGroups;

            return new RestUserGroup(userGroup);
        }

        return undefined;
    }

    public async updateUser(user: RestUser): Promise<RestUser> {
        const updatedUser = await this.request<RestUser>("PUT", `/api/users/${user.email}`, user);

        return new RestUser(updatedUser);
    }

    // currently no tests for this... or msw handlers for this either
    public async updateUserFields(email: string, ...fields: any): Promise<RestUser> {
        const updatedUser = await this.request<RestUser>("PATCH", `/api/users/${email}`, ...fields);
        return new RestUser(updatedUser);
    }

    public async addOrganizationIdentityProvider({
        organizationId,
        issuer,
        ssoUrl,
        certificate
    }: {
        organizationId: string;
        issuer: string;
        ssoUrl: string;
        certificate: File;
    }): Promise<RestIdentityProvider> {
        const formData = new FormData();

        formData.append("issuer", issuer);
        formData.append("ssoUrl", ssoUrl);
        formData.append("certificate", certificate);

        const accessToken = await this.resolveAccessToken();
        // Manually fetch here so the file is not stringified in the body.
        const response = await fetch(`${this.baseURL}/api/organizations/${organizationId}/identityprovider/saml`, {
            method: "POST",
            body: formData,
            headers: {
                // Do NOT explicitly set the content-type here, per
                // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sect4
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (response.status === 500) {
            throw new Error("Failed request: (500)");
        }

        const idp = await response.json();

        if (!response.ok) {
            // Default error message if the HTTP response does not contain a more
            // specific message.
            let message = UNEXPECTED_ERROR;

            if (idp instanceof Array) {
                message = idp[0].message;
            }

            throw new Error(message);
        }

        return new RestIdentityProvider(idp);
    }

    public async addSAMLIdentityProvider({
        idpName,
        organizationId,
        issuer,
        ssoUrl,
        certificate
    }: {
        idpName: string;
        organizationId: string;
        issuer: string;
        ssoUrl: string;
        certificate: File;
    }): Promise<RestIdentityProvider> {
        const formData = new FormData();

        formData.append("idpName", idpName);
        formData.append("issuer", issuer);
        formData.append("ssoUrl", ssoUrl);
        formData.append("certificate", certificate);

        const accessToken = await this.resolveAccessToken();
        // Manually fetch here so the file is not stringified in the body.
        const response = await fetch(`${this.baseURL}/api/organizations/${organizationId}/identityprovider/saml`, {
            method: "POST",
            body: formData,
            headers: {
                // Do NOT explicitly set the content-type here, per
                // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sect4
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (response.status === 500) {
            throw new Error("Failed request: (500)");
        }

        const idp = await response.json();

        if (!response.ok) {
            let message = UNEXPECTED_ERROR;

            if (idp instanceof Array) {
                message = idp[0].message;
            }

            throw new Error(message);
        }

        return new RestIdentityProvider(idp);
    }

    public async uploadSAMLIdentityProviderCertificate({
        organizationId,
        identityProviderId,
        certificate
    }: {
        organizationId: string;
        identityProviderId: string;
        certificate: File;
    }): Promise<{ expiresAt: string | null }> {
        const formData = new FormData();
        formData.append("certificate", certificate);

        const accessToken = await this.resolveAccessToken();
        const url = `${this.baseURL}/api/organizations/${organizationId}/identityproviders/${identityProviderId}/certificate`;

        const response = await fetch(url, {
            method: "POST",
            body: formData,
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        });

        let result: any = await response.json();

        if (!response.ok) {
            let message = UNEXPECTED_ERROR;
            if (result) {
                if (Array.isArray(result) && result[0]?.message) {
                    message = result[0].message;
                } else if (result.message) {
                    message = result.message;
                }
            }
            throw new Error(message);
        }

        return { expiresAt: result?.CertificateExpiration ?? null };
    }

    public async addOIDCIdentityProvider({
        authorizationUrl,
        clientId,
        clientSecret,
        idpName,
        issuerUrl,
        jwksUrl,
        organizationId,
        provider,
        tokenUrl,
        userInfoUrl,
        wellKnownUrl: wellknownMetadataUrl
    }: {
        authorizationUrl?: string;
        clientId: string;
        clientSecret: string;
        idpName: string;
        issuerUrl?: string;
        jwksUrl?: string;
        organizationId: string;
        provider: string;
        tokenUrl?: string;
        userInfoUrl?: string;
        wellKnownUrl?: string;
    }): Promise<RestIdentityProvider> {
        const body = {
            authorizationUrl,
            clientId,
            clientSecret,
            idpName,
            issuerUrl,
            jwksUrl,
            organizationId,
            provider,
            tokenUrl,
            userInfoUrl,
            wellknownMetadataUrl
        };

        const accessToken = await this.resolveAccessToken();
        // Manually fetch here so the file is not stringified in the body.
        const response = await fetch(`${this.baseURL}/api/organizations/${organizationId}/identityprovider/oidc`, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                // Do NOT explicitly set the content-type here, per
                // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sect4
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (response.status === 500) {
            throw new Error("Failed request: (500)");
        }

        const idp = await response.json();

        if (!response.ok) {
            // Default error message if the HTTP response does not contain a more
            // specific message.
            let message = "An unexpected error occurred. Please try again.";

            if (idp instanceof Array) {
                message = idp[0].message;
            }

            throw new Error(message);
        }

        return new RestIdentityProvider(idp);
    }

    public async deleteOrganizationIdentityProvider({
        id,
        organizationId
    }: {
        id: string;
        organizationId: string;
    }): Promise<void> {
        return await this.request<void>("DELETE", `/api/organizations/${organizationId}/identityproviders/${id}`);
    }

    public async deleteOrganizationIdentityProviders({ organizationId }: { organizationId: string }): Promise<void> {
        return await this.request<void>("DELETE", `/api/organizations/${organizationId}/identityprovider`);
    }

    public async getOrganizationIdentityProviders({
        organizationId
    }: {
        organizationId: string;
    }): Promise<RestIdentityProvider[] | null> {
        const response = await this.request<{ Data: RestIdentityProvider[] }>(
            "GET",
            `/api/organizations/${organizationId}/identityproviders`
        );

        if (!response?.Data) {
            return null;
        }
        const idps = response.Data;

        return idps?.map((orgIdp) => new RestIdentityProvider(orgIdp));
    }

    public async getOrganizationDomains({ organizationId }: { organizationId: string }): Promise<string[]> {
        const orgDomains: any = await this.request<IRestResponseResult<RestIdentityProvider>>(
            "GET",
            `/api/organizations/${organizationId}/domains`
        );

        if (!orgDomains) {
            return [];
        }

        return orgDomains.Urls;
    }
    public async updateOrganization({
        organizationId,
        mfaEnabled
    }: {
        organizationId: string;
        mfaEnabled?: boolean;
    }): Promise<any> {
        const updates: any = {};
        if (mfaEnabled !== undefined) {
            updates["mfaEnabled"] = mfaEnabled;
        }

        return this.request<IRestResponseResult<any>>("PUT", `/api/organizations/${organizationId}`, updates);
    }

    public async updateOrganizationDomains({
        organizationId,
        domains
    }: {
        organizationId: string;
        domains: string[];
    }): Promise<string[]> {
        const response: any = await this.request<IRestResponseResult<RestIdentityProvider>>(
            "PUT",
            `/api/organizations/${organizationId}/domains`,
            { urls: domains }
        );

        if (!response.Urls) {
            return [];
        }

        return response.Urls;
    }

    public async updateIdentityProviderDomains({
        organizationId,
        identityProviderId,
        domains
    }: {
        organizationId: string;
        identityProviderId: string;
        domains: string[];
    }): Promise<RestIdentityProvider> {
        const response = await this.request<IRestResponseResult<RestIdentityProvider>>(
            "PUT",
            `/api/organizations/${organizationId}/identityproviders/${identityProviderId}/domains`,
            { urls: domains }
        );
        return new RestIdentityProvider(response);
    }

    public async enableScimForIdentityProvider(params: {
        organizationId: string;
        identityProviderId: string;
    }): Promise<RestIdentityProvider> {
        const { organizationId, identityProviderId } = params;

        const endpoint = `/api/organizations/${organizationId}/identityproviders/${identityProviderId}/scim`;
        const response = await this.request<IRestResponseResult<RestIdentityProvider>>("POST", endpoint);
        return new RestIdentityProvider(response);
    }

    public async getUserAdminCenterInstancePermissions({
        email,
        orgId
    }: {
        email: string;
        orgId?: string;
    }): Promise<RestUserInstancePermission[]> {
        const params = new URLSearchParams();
        params.append("productId", ADMINCENTER_PRODUCT_ID);

        if (orgId) {
            params.append("customerId", orgId);
        }

        const adminCenterPermissions = await this.request<IRestResponseResult<RestUserInstancePermission>>(
            "GET",
            `/api/users/${email}/instancepermissions?${params.toString()}`
        );

        const instancePermissions = adminCenterPermissions.items.map(
            (instance) => new RestUserInstancePermission(instance)
        );

        return instancePermissions;
    }

    public async getUserInstancePermissions({ email }: { email: string }): Promise<RestUserInstancePermission[]> {
        const permissions = await this.request<IRestResponseResult<RestUserInstancePermission>>(
            "GET",
            `/api/users/${email}/instancepermissions?size=${ALL_PAGES_SIZE}`
        );

        return permissions.items.map((instance) => new RestUserInstancePermission(instance));
    }

    public async getRoles({
        organizationId,
        productId,
        projectId,
        instanceId,
        size = DEFAULT_PAGE_SIZE,
        page = 1
    }: {
        organizationId: string;
        productId?: string;
        projectId?: string;
        instanceId?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestRole>> {
        const search = new URLSearchParams();
        // don't want to send `undefined` values for productId, attributeId, and instanceId
        productId && search.append("productId", productId);
        instanceId && search.append("instanceId", instanceId);
        projectId && projectId !== ALL_PROJECTS_ID && search.append("attributeId", projectId);

        search.append("isActive", "true");
        search.append("size", `${size}`);
        search.append("offset", `${(page - 1) * size}`);

        const roles = await this.request<IRestResponseResult<RestRole>>(
            "GET",
            `/api/organizations/${organizationId}/roles?${search.toString()}`
        );

        return new Collection<RestRole>({
            items: roles.items.map((role: RestRole) => new RestRole(role)),
            totalCount: roles.totalItemCount
        });
    }

    public async searchRoles({
        organizationId,
        productId,
        instanceId,
        searchQuery,
        size = DEFAULT_PAGE_SIZE,
        page = 1,
        projectId
    }: {
        organizationId: string;
        productId?: string;
        instanceId?: string;
        searchQuery?: string;
        size?: number;
        page?: number;
        projectId?: string;
    }): Promise<Collection<RestRole>> {
        const search = new URLSearchParams();
        // don't want to send `undefined` values for productId, attributeId, and instanceId
        productId && search.append("productId", productId);
        instanceId && search.append("instanceId", instanceId);
        projectId && search.append("attributeId", projectId);

        search.append("isActive", "true");
        search.append("size", `${size}`);
        search.append("offset", `${(page - 1) * size}`);

        searchQuery && search.append("name", searchQuery);

        const roles = await this.request<IRestResponseResult<RestRole>>(
            "GET",
            `/api/organizations/${organizationId}/roles/search?${search.toString()}`
        );

        return new Collection<RestRole>({
            items: roles.items.map((role: RestRole) => new RestRole(role)),
            totalCount: roles.totalItemCount
        });
    }

    public async createRole(roleDetails: RoleCreationParameters): Promise<RestRole> {
        const { organizationId, instances, product, roleType, ...rest } = roleDetails;
        const isProductRole = roleType === CUSTOMER_DEFINED_PRODUCT_ROLE || instances[0].name === ALL;

        const role = await this.request<RestRole>("POST", "/api/roles", {
            ...rest,
            productId: product.id,
            roleType: isProductRole ? CUSTOMER_DEFINED_PRODUCT_ROLE : CUSTOMER_DEFINED_INSTANCE_ROLE,
            targetIds: isProductRole ? [organizationId] : instances.map((instance) => instance.id)
        });

        return new RestRole(role);
    }

    public async deleteRole({ roleId }: { roleId: string }): Promise<void> {
        return this.request<void>("DELETE", `/api/roles/${roleId}`);
    }

    public async updateRole(roleDetails: RoleCreationParameters): Promise<RestRole> {
        const { organizationId, instances, product, id: roleId, ...rest } = roleDetails;
        const isProductRole = instances[0].name === ALL;
        const role = await this.request<RestRole>("PUT", `/api/roles/${roleId}`, {
            ...rest,
            productId: product.id,
            roleType: isProductRole ? CUSTOMER_DEFINED_PRODUCT_ROLE : CUSTOMER_DEFINED_INSTANCE_ROLE,
            targetIds: isProductRole ? [organizationId] : instances.map((instance) => instance.id)
        });

        return new RestRole(role);
    }

    public async getAttributes(parameters: GetAttributeParameters): Promise<Collection<RestAttribute>> {
        const { isActive, instanceId, key, offset, productId, size, types, name } = parameters;

        const params = new URLSearchParams();

        if (productId) params.append("productId", `${productId}`);
        params.append("size", `${size}`);
        params.append("offset", `${offset}`);

        if (typeof isActive === "boolean") params.append("isActive", `${isActive}`);
        if (instanceId) params.append("instanceId", instanceId);
        if (key) params.append("key", key);
        if (name) params.append("name", name);

        if (types && types.length) {
            types.forEach((type) => {
                params.append("types", type);
            });
        }

        const url = `/api/attributes?${params.toString()}`;

        const attributes = await this.request<Collection<RestAttribute>>("GET", url);

        return new Collection<RestAttribute>({
            items: attributes.items.map((attribute: RestAttribute) => new RestAttribute(attribute)),
            totalCount: attributes.totalCount
        });
    }

    public async getAttribute(id: string): Promise<RestAttribute> {
        const attribute = await this.request<RestAttribute>("GET", `/api/attributes/${id}`);
        return new RestAttribute(attribute);
    }

    public async getInvitations({
        createdBy,
        inviteeEmail,
        organizationId,
        size = 20,
        page = 1
    }: {
        createdBy?: string;
        inviteeEmail?: string;
        organizationId?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestInvitation>> {
        const params = new URLSearchParams();
        if (organizationId) {
            params.append("organizationId", `${organizationId}`);
        }
        if (createdBy) {
            params.append("createdBy", `${createdBy}`);
        }
        if (inviteeEmail) {
            params.append("inviteeEmail", `${inviteeEmail}`);
        }
        params.append("size", `${size}`);
        params.append("offset", `${(page - 1) * size}`);

        const invitations = await this.request<IRestResponseResult<RestInvitation>>(
            "GET",
            `/api/invitations?${params.toString()}`
        );

        return new Collection<RestInvitation>({
            items: invitations.items.map((invitation: RestInvitation) => new RestInvitation(invitation)),
            totalCount: invitations.totalItemCount
        });
    }

    public async createInvitation(invitation: Partial<RestInvitation>): Promise<RestInvitation> {
        const invitationToSend = {
            ...invitation,
            userGroupIds: invitation.userGroups ? invitation.userGroups.map((ug) => ug.id) : []
        };
        const response = await this.request<IRestResponseResult<RestInvitation>>(
            "POST",
            "/api/invitations",
            invitationToSend
        );
        return new RestInvitation(response);
    }

    public async revokeInvitation(invitationId: string, requireAcceptance: boolean): Promise<void> {
        return await this.request<undefined>("POST", `/api/invitations/${invitationId}/revoke`, {
            RequireAcceptance: requireAcceptance
        });
    }

    public async resendInvitation(invitationId: string): Promise<void> {
        return await this.request<undefined>("POST", `/api/invitations/${invitationId}/resend`, {
            RequireAcceptance: true
        });
    }

    public async updateInstance({
        instance
    }: {
        instance: Partial<RestOrganizationProduct>;
    }): Promise<RestProductInstance> {
        const response = await this.request<RestProductInstance>("PATCH", `/api/instances/${instance.id}`, instance);
        return new RestProductInstance(response);
    }

    public async searchCustomers({ query }: { query: string }): Promise<Collection<RestCustomer>> {
        const params = new URLSearchParams();
        params.append("query", query);

        const response = await this.request<Collection<RestCustomer>>("GET", `/api/customers?${params.toString()}`);

        return new Collection<RestCustomer>({
            items: response.items?.map((r) => new RestCustomer(r)),
            totalCount: response.totalCount
        });
    }

    public async getProviders(): Promise<string[]> {
        return await this.request<string[]>("GET", "/api/providers");
    }

    public async getUserListByProduct({
        organizationId,
        page = 1,
        productIds,
        searchQuery = ""
    }: {
        organizationId: string;
        page?: number;
        productIds: string[];
        searchQuery?: string;
    }): Promise<Collection<RestUser>> {
        const orgProducts = await this.getOrganizationProductInstances({ organizationId });
        const products = orgProducts.products.filter((p) => productIds.includes(p.id));
        const instances = products.flatMap((p) => p.instances);
        const instanceIds = instances.map((i) => i.id);

        return this.getUserListByInstance({ instanceIds, organizationId, page, searchQuery });
    }

    public async getUserListByInstance({
        attributeIds = [],
        instanceIds,
        organizationId,
        page = 1,
        roleIds = [],
        searchQuery = ""
    }: {
        attributeIds?: string[];
        instanceIds: string[];
        organizationId?: string | undefined;
        page?: number;
        roleIds?: string[];
        searchQuery?: string;
    }): Promise<Collection<RestUser>> {
        const result = await this.getUserGroupsByInstance({
            attributeIds,
            groupTypes: [GROUP_TYPES.PRODUCT],
            instanceIds,
            organizationId,
            roleIds
        });

        const groups = result?.items;
        if (!groups?.length) {
            return Promise.resolve(new Collection<RestUser>({ items: [] }));
        }

        const groupIds = groups.map((ug) => ug.id);
        return await this.getUsers({
            userGroupIds: groupIds,
            query: searchQuery,
            size: DEFAULT_PAGE_SIZE,
            page,
            includeExternalStatus: true
        });
    }

    public async getUserListByProject({
        organizationId,
        attributeIds,
        roleIds = [],
        page = 1,
        searchQuery = ""
    }: {
        organizationId: string;
        attributeIds: string[];
        roleIds?: string[];
        page?: number;
        searchQuery?: string;
    }): Promise<Collection<RestUser>> {
        const result = await this.getUserGroupsByAttributeId({
            organizationId,
            attributeIds,
            roleIds,
            groupTypes: [GROUP_TYPES.PRODUCT]
        });
        const groups = result?.items;
        const groupIds = groups.map((ug) => ug.id);

        if (groupIds.length) {
            return await this.getUsers({
                userGroupIds: groupIds,
                query: searchQuery,
                size: DEFAULT_PAGE_SIZE,
                page
            });
        } else {
            return Promise.resolve({ items: [], totalCount: 0 });
        }
    }

    public async getUserGroupsByInstance({
        attributeIds = [],
        groupTypes = [],
        instanceIds,
        organizationId,
        roleIds = []
    }: {
        attributeIds: string[];
        groupTypes?: string[];
        instanceIds: string[];
        organizationId?: string | undefined;
        roleIds: string[];
    }): Promise<Collection<RestUserGroup>> {
        const params = new URLSearchParams();

        if (!!organizationId) params.append("organizationId", organizationId);

        instanceIds.forEach((id) => {
            params.append("instanceIds", id);
        });

        groupTypes.forEach((type) => {
            params.append("groupTypes", type);
        });

        roleIds.forEach((type) => {
            params.append("roleIds", type);
        });

        attributeIds.forEach((type) => {
            params.append("attributeIds", type);
        });

        params.append("matchAnyInstanceId", "true");
        params.append("size", `${ALL_PAGES_SIZE}`);

        const result = await this.request<Collection<RestUserGroup>>("GET", `/api/usergroups?${params.toString()}`);

        return new Collection<RestUserGroup>({
            items: result?.items.map((userGroup: RestUserGroup) => new RestUserGroup(userGroup)),
            totalCount: result.totalCount
        });
    }

    public async getUserGroupsByAttributeId({
        organizationId,
        attributeIds,
        roleIds = [],
        groupTypes = []
    }: {
        organizationId: string;
        attributeIds: string[];
        roleIds?: string[];
        groupTypes?: string[];
    }): Promise<Collection<RestUserGroup>> {
        const params = new URLSearchParams();
        params.append("organizationId", organizationId);
        attributeIds.forEach((id) => {
            params.append("attributeIds", id);
        });
        groupTypes.forEach((type) => {
            params.append("groupTypes", type);
        });
        roleIds?.forEach((id) => {
            params.append("roleIds", id);
        });

        const result = await this.request<Collection<RestUserGroup>>(
            "GET",
            `/api/usergroups?${params.toString()}&size=${ALL_PAGES_SIZE}`
        );

        return new Collection<RestUserGroup>({
            items: result?.items.map((userGroup: RestUserGroup) => new RestUserGroup(userGroup)),
            totalCount: result.totalCount
        });
    }

    public async migrateUser({ email, organizationId }: { email: string; organizationId: string }): Promise<RestUser> {
        const updatedUser = await this.request<RestUser>("POST", `/api/users/${email}/migrate/${organizationId}`);
        return new RestUser(updatedUser);
    }

    public async getOrganization({ id }: { id: string }): Promise<RestOrganization> {
        const org = await this.request<RestOrganization>("GET", `/api/organizations/${id}`);
        return new RestOrganization(org);
    }

    public async getBillingNotifications({
        organizationId
    }: {
        organizationId: string;
    }): Promise<{ Settings: { billingNotifications: BillingNotification[] } }> {
        return await this.request<{ Settings: { billingNotifications: BillingNotification[] } }>(
            "GET",
            `/api/organizations/${organizationId}/billingnotifications`
        );
    }

    public async updateBillingNotification({
        notificationId,
        organizationId,
        updates
    }: {
        notificationId: string;
        organizationId: string;
        updates: Pick<BillingNotification, "enabled" | "recipients">;
    }): Promise<BillingNotification> {
        return this.request("PUT", `/api/organizations/${organizationId}/billingnotifications/${notificationId}`, {
            ...updates
        });
    }

    public async getLocalLoginSettings({
        organizationId
    }: {
        organizationId: string;
    }): Promise<{ Settings: LocalLoginSettings }> {
        return await this.request<{ Settings: LocalLoginSettings }>(
            "GET",
            `/api/organizations/${organizationId}/settings/locallogin`
        );
    }

    public async getOrganizationSettings({
        organizationId
    }: {
        organizationId: string;
    }): Promise<{ Settings: RestOrganizationSettings }> {
        return await this.request<{ Settings: RestOrganizationSettings }>(
            "GET",
            `/api/organizations/${organizationId}/settings`
        );
    }

    public async updateGenAISettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean };
    }): Promise<{ enabled: boolean }> {
        return await this.request<{ enabled: boolean }>(
            "PUT",
            `/api/organizations/${organizationId}/settings/genai`,
            settings
        );
    }

    public async updateLocalLoginSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; mfaEnabled: boolean };
    }): Promise<LocalLoginSettings> {
        return await this.request<LocalLoginSettings>(
            "PUT",
            `/api/organizations/${organizationId}/settings/locallogin`,
            { ...settings }
        );
    }

    public async updateDeveloperSettings({
        organizationId,
        settings
    }: {
        organizationId: string;
        settings: { enabled: boolean; vendorName: string };
    }): Promise<{ enabled: boolean; vendorName: string } | undefined> {
        return await this.request<{ enabled: boolean; vendorName: string } | undefined>(
            "PUT",
            `/api/organizations/${organizationId}/settings/developer`,
            settings
        );
    }

    public async updateOrganizationSettings({
        organizationId,
        updates
    }: {
        organizationId: string;
        updates: Partial<RestOrganizationSettings>;
    }): Promise<{ Settings: RestOrganizationSettings }> {
        return await this.request<{ Settings: RestOrganizationSettings }>(
            "PATCH",
            `/api/organizations/${organizationId}/settings`,
            updates
        );
    }

    public async addScimInstance(params: { orgId: string; identityProviderId: string }): Promise<any> {
        const { orgId, identityProviderId } = params;

        const endpoint = `/api/organizations/${orgId}/identityproviders/${identityProviderId}/scim`;
        return this.request<any>("POST", endpoint);
    }

    public async updateSAMLIdentityProvider({
        organizationId,
        id,
        idpName,
        issuer,
        ssoUrl
    }: {
        organizationId: string;
        id: string;
        idpName: string;
        issuer: string;
        ssoUrl: string;
    }): Promise<RestIdentityProvider> {
        const body = {
            idpName,
            issuer,
            ssoUrl
        };

        const accessToken = await this.resolveAccessToken();
        const response = await fetch(
            `${this.baseURL}/api/organizations/${organizationId}/identityproviders/${id}/saml`,
            {
                method: "PUT",
                body: JSON.stringify(body),
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json"
                }
            }
        );

        if (response.status === 500) {
            throw new Error("Failed request: (500)");
        }

        const idp = await response.json();

        if (!response.ok) {
            // Default error message if the HTTP response does not contain a more
            // specific message.
            let message = UNEXPECTED_ERROR;

            if (idp instanceof Array) {
                message = idp[0].message;
            }

            throw new Error(message);
        }

        return new RestIdentityProvider(idp);
    }

    public async removeSCIMForIdentityProvider({
        identityProviderId,
        organizationId
    }: {
        identityProviderId: string;
        organizationId: string;
    }): Promise<void> {
        const endpoint = `/api/organizations/${organizationId}/identityproviders/${identityProviderId}/scim`;
        return this.request<void>("DELETE", endpoint);
    }

    public async uploadUsersCsv({
        organizationId,
        file,
        userGroupIds
    }: {
        organizationId: string;
        file: File;
        userGroupIds?: string[];
    }): Promise<void> {
        const formData = new FormData();
        formData.append("file", file);

        // The backend binds the repeated form field as `userGroupId` (singular), even though it
        // accepts multiple values. Keep the wire format isolated here.
        userGroupIds?.forEach((id) => formData.append("userGroupId", id));

        const accessToken = await this.resolveAccessToken();
        // Manually fetch here so the file is not stringified in the body.
        const response = await fetch(`${this.baseURL}/api/organizations/${organizationId}/users/bulk/upload`, {
            method: "POST",
            body: formData,
            headers: {
                // Do NOT explicitly set the content-type here, per
                // https://developer.mozilla.org/en-US/docs/Web/API/FormData/Using_FormData_Objects#sect4
                Authorization: `Bearer ${accessToken}`
            }
        });

        if (!response.ok) {
            let message = UNEXPECTED_ERROR;

            const responseText = await response.text();

            if (responseText) {
                try {
                    const body = JSON.parse(responseText);

                    if (body instanceof Array && body.length > 0) {
                        message = body.map((e: { message: string }) => e.message).join("\n");
                    } else if (body?.message) {
                        message = body.message;
                    }
                } catch {
                    // Ignore non-JSON error bodies and fall back to the default message.
                }
            }

            throw new Error(message);
        }
    }

    /**
     * Lists bulk user CSV uploads for an organization, most recent first (sorting is server-side).
     *
     * Note the plural `uploads` segment: the singular `/users/bulk/upload` is the POST endpoint
     * used by {@link uploadUsersCsv}. The API caps `size` at 200 and requires a zero-based
     * `offset`. Restricted to Super Admins of the target organization (403 otherwise).
     */
    public async getBulkUploads({
        organizationId,
        originalFileName,
        size = DEFAULT_PAGE_SIZE,
        page = 1
    }: {
        organizationId: string;
        originalFileName?: string;
        size?: number;
        page?: number;
    }): Promise<Collection<RestBulkUpload>> {
        // `page` reaches here straight from a URL query param, so it can be 0, negative or NaN.
        // Left unguarded that produces a negative or NaN `offset` and the API either pages wrongly
        // or errors. Note `Math.max(1, NaN)` is NaN, hence the explicit finite check.
        const safeSize = Number.isFinite(size) && size >= 1 ? Math.floor(size) : DEFAULT_PAGE_SIZE;
        const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1;

        const params = new URLSearchParams();
        params.append("size", `${safeSize}`);
        params.append("offset", `${(safePage - 1) * safeSize}`);
        if (originalFileName) {
            params.append("originalFileName", originalFileName);
        }

        const uploads = await this.request<IRestResponseResult<RestBulkUpload>>(
            "GET",
            `/api/organizations/${organizationId}/users/bulk/uploads?${params.toString()}`
        );

        return new Collection<RestBulkUpload>({
            items: uploads.items.map((upload: RestBulkUpload) => new RestBulkUpload(upload)),
            totalCount: uploads.totalItemCount
        });
    }

    /**
     * Fetches a single bulk user CSV upload by its file identifier.
     *
     * Returns the same `BulkUploadStatusResponse` shape as {@link getBulkUploads} — the detail
     * endpoint carries no extra fields, it just reads one row fresh, which is what makes polling
     * an in-flight upload worthwhile.
     *
     * Restricted to Super Admins of the target organization (403 otherwise). A `fileIdentifier`
     * belonging to another organization 404s rather than leaking across the partition.
     */
    public async getBulkUpload({
        organizationId,
        fileIdentifier
    }: {
        organizationId: string;
        fileIdentifier: string;
    }): Promise<RestBulkUpload> {
        // Both segments land in the URL path and reach here from a route/query param, so encode
        // them rather than trusting their shape.
        const upload = await this.request<RestBulkUpload>(
            "GET",
            `/api/organizations/${encodeURIComponent(organizationId)}/users/bulk/uploads/${encodeURIComponent(
                fileIdentifier
            )}`
        );

        return new RestBulkUpload(upload);
    }
}
